import { DocumentType } from '../../types/document-type';
import { documentTypeName, TranslateFn } from '../document-type-label';

export type DocumentTypeGroup =
  | 'sales-pre'
  | 'logistics'
  | 'invoicing'
  | 'workshop'
  | 'purchase'
  | 'other';

export interface DocumentTypeGroupMeta {
  id: DocumentTypeGroup;
  label: string;
  order: number;
}

export const DOCUMENT_TYPE_GROUPS: Record<DocumentTypeGroup, DocumentTypeGroupMeta> = {
  'sales-pre': { id: 'sales-pre', label: 'Verkauf', order: 1 },
  logistics: { id: 'logistics', label: 'Logistik', order: 2 },
  invoicing: { id: 'invoicing', label: 'Fakturierung', order: 3 },
  workshop: { id: 'workshop', label: 'Werkstatt', order: 4 },
  purchase: { id: 'purchase', label: 'Einkauf', order: 5 },
  // Catch-all: /document/query/types is customer-scoped (Ticket 449072), so a
  // supported type this map does not know must still reach the user.
  other: { id: 'other', label: 'Weitere', order: 99 },
};

export const DOCUMENT_TYPE_TO_GROUP: Record<string, DocumentTypeGroup> = {
  OFFER: 'sales-pre',
  BLANKET_ORDER: 'sales-pre',
  SALES_ORDER: 'sales-pre',
  ROUTING_SLIP: 'logistics',
  COMMISSION_NOTE: 'logistics',
  DELIVERY_NOTE: 'logistics',
  MATERIAL_DELIVERY_NOTE: 'logistics',
  SALES_VOUCHER: 'invoicing',
  SALES_VOUCHER_ALL: 'invoicing',
  INVOICE: 'invoicing',
  CASH_SALE_INVOICE: 'invoicing',
  CREDIT_INVOICE: 'invoicing',
  WORK_ORDER: 'workshop',
  CONSOLIDATED_MEASUREMENT_REPORT: 'workshop',
  PURCHASE_ORDER: 'purchase',
  BLANKET_PURCHASE_ORDER: 'purchase',
  PURCHASE_DELIVERY_NOTE: 'purchase',
  PURCHASE_WEIGHT_RECEIPT: 'purchase',
  INCOMING_PRODUCTS: 'purchase',
  INCOMING_PRODUCT_TESTING: 'purchase',
  INVOICE_VERIFICATION: 'purchase',
  DEBIT_ADVICE: 'purchase',
  PURCHASE_INVOICE: 'purchase',
};

export interface DocumentTypeVisuals {
  bgColor: string;
  lightBgColor: string;
  textColor: string;
  icon: string;
  order: number;
}

export const DOCUMENT_TYPE_META: Record<string, DocumentTypeVisuals> = {
  OFFER: {
    bgColor: 'bg-[#5867E8]!',
    lightBgColor: 'bg-[#E1E4FA]!',
    textColor: 'text-[#5867E8]',
    icon: 'fa-regular fa-file-lines',
    order: 1,
  },
  BLANKET_ORDER: {
    bgColor: 'bg-[#0176D3]!',
    lightBgColor: 'bg-[#D2E8F8]!',
    textColor: 'text-[#0176D3]',
    icon: 'fa-regular fa-file-invoice',
    order: 2,
  },
  SALES_ORDER: {
    bgColor: 'bg-[#066AFE]!',
    lightBgColor: 'bg-[#D1E2FE]!',
    textColor: 'text-[#066AFE]',
    icon: 'fa-regular fa-file-invoice',
    order: 3,
  },
  ROUTING_SLIP: {
    bgColor: 'bg-[#107CAD]!',
    lightBgColor: 'bg-[#D2EAF5]!',
    textColor: 'text-[#107CAD]',
    icon: 'fa-regular fa-route',
    order: 5,
  },
  COMMISSION_NOTE: {
    bgColor: 'bg-[#1A998A]!',
    lightBgColor: 'bg-[#D5EFEB]!',
    textColor: 'text-[#1A998A]',
    icon: 'fa-regular fa-clipboard-list',
    order: 6,
  },
  SALES_VOUCHER: {
    bgColor: 'bg-[#FCC003]!',
    lightBgColor: 'bg-[#FFF1C2]!',
    textColor: 'text-[#7A5C00]',
    icon: 'fa-regular fa-receipt',
    order: 7,
  },
  SALES_VOUCHER_ALL: {
    bgColor: 'bg-[#FCC003]!',
    lightBgColor: 'bg-[#FFF1C2]!',
    textColor: 'text-[#7A5C00]',
    icon: 'fa-regular fa-receipt',
    order: 7.1,
  },
  DELIVERY_NOTE: {
    bgColor: 'bg-[#45C65A]!',
    lightBgColor: 'bg-[#DBF3DF]!',
    textColor: 'text-[#1F7A2E]',
    icon: 'fa-regular fa-truck-front',
    order: 8,
  },
  INVOICE: {
    bgColor: 'bg-[#9050E9]!',
    lightBgColor: 'bg-[#E8DBFB]!',
    textColor: 'text-[#9050E9]',
    icon: 'fa-regular fa-file-invoice-dollar',
    order: 9,
  },
  CASH_SALE_INVOICE: {
    bgColor: 'bg-[#BA01FF]!',
    lightBgColor: 'bg-[#F0D4FE]!',
    textColor: 'text-[#BA01FF]',
    icon: 'fa-regular fa-money-bill',
    order: 9.1,
  },
  CREDIT_INVOICE: {
    bgColor: 'bg-[#FE5C4C]!',
    lightBgColor: 'bg-[#FEDBD6]!',
    textColor: 'text-[#FE5C4C]',
    icon: 'fa-regular fa-file-circle-minus',
    order: 9.2,
  },
  MATERIAL_DELIVERY_NOTE: {
    bgColor: 'bg-[#2E844A]!',
    lightBgColor: 'bg-[#D4E9DC]!',
    textColor: 'text-[#2E844A]',
    icon: 'fa-regular fa-dolly',
    order: 10,
  },
  WORK_ORDER: {
    bgColor: 'bg-[#FE9339]!',
    lightBgColor: 'bg-[#FEE2C8]!',
    textColor: 'text-[#A85100]',
    icon: 'fa-regular fa-screwdriver-wrench',
    order: 11,
  },
  CONSOLIDATED_MEASUREMENT_REPORT: {
    bgColor: 'bg-[#0D9DDA]!',
    lightBgColor: 'bg-[#CFEEF9]!',
    textColor: 'text-[#0D9DDA]',
    icon: 'fa-regular fa-ruler-combined',
    order: 12,
  },
  PURCHASE_ORDER: {
    bgColor: 'bg-[#E1167F]!',
    lightBgColor: 'bg-[#FBD3E6]!',
    textColor: 'text-[#E1167F]',
    icon: 'fa-regular fa-cart-shopping',
    order: 20,
  },
  BLANKET_PURCHASE_ORDER: {
    bgColor: 'bg-[#B60554]!',
    lightBgColor: 'bg-[#F4CDDE]!',
    textColor: 'text-[#B60554]',
    icon: 'fa-regular fa-cart-flatbed',
    order: 21,
  },
  PURCHASE_DELIVERY_NOTE: {
    bgColor: 'bg-[#EE6F46]!',
    lightBgColor: 'bg-[#FCDDD3]!',
    textColor: 'text-[#C24D26]',
    icon: 'fa-regular fa-truck-ramp-box',
    order: 22,
  },
  PURCHASE_WEIGHT_RECEIPT: {
    bgColor: 'bg-[#D83A00]!',
    lightBgColor: 'bg-[#F8D2C2]!',
    textColor: 'text-[#D83A00]',
    icon: 'fa-regular fa-weight-scale',
    order: 23,
  },
  INCOMING_PRODUCTS: {
    bgColor: 'bg-[#7F8CED]!',
    lightBgColor: 'bg-[#E0E3FB]!',
    textColor: 'text-[#5867E8]',
    icon: 'fa-regular fa-boxes-stacked',
    order: 24,
  },
  INCOMING_PRODUCT_TESTING: {
    bgColor: 'bg-[#08ABED]!',
    lightBgColor: 'bg-[#CFEDFB]!',
    textColor: 'text-[#08ABED]',
    icon: 'fa-regular fa-clipboard-check',
    order: 25,
  },
  INVOICE_VERIFICATION: {
    bgColor: 'bg-[#9602C7]!',
    lightBgColor: 'bg-[#E8CDF4]!',
    textColor: 'text-[#9602C7]',
    icon: 'fa-regular fa-magnifying-glass-dollar',
    order: 26,
  },
  DEBIT_ADVICE: {
    bgColor: 'bg-[#EA001E]!',
    lightBgColor: 'bg-[#FCCFD3]!',
    textColor: 'text-[#EA001E]',
    icon: 'fa-regular fa-file-circle-exclamation',
    order: 27,
  },
  PURCHASE_INVOICE: {
    bgColor: 'bg-[#681DC9]!',
    lightBgColor: 'bg-[#DCCFF4]!',
    textColor: 'text-[#681DC9]',
    icon: 'fa-regular fa-file-invoice-dollar',
    order: 28,
  },
};

/**
 * `translate` is optional so existing `.map(mapDocumentType)` call sites keep
 * working — Array.map hands the index as the second argument, hence the
 * function check rather than a truthiness test.
 */
/** Neutral visuals for a supported type this frontend has no design for yet —
 *  it sorts last, but it is shown rather than dropped. */
export const DOCUMENT_TYPE_FALLBACK_VISUALS: DocumentTypeVisuals = {
  bgColor: 'bg-[#6B7280]!',
  lightBgColor: 'bg-[#E5E7EB]!',
  textColor: 'text-[#374151]',
  icon: 'fa-regular fa-file-lines',
  order: Number.MAX_SAFE_INTEGER,
};

export function mapDocumentType(
  apiType: DocumentType,
  translate?: TranslateFn | unknown
): DocumentType {
  const meta = DOCUMENT_TYPE_META[apiType.type] ?? DOCUMENT_TYPE_FALLBACK_VISUALS;
  const resolve = typeof translate === 'function' ? (translate as TranslateFn) : null;
  return {
    ...meta,
    ...apiType,
    // Customer wording (Ticket 448972) overrides the backend default.
    displayName: resolve ? documentTypeName(apiType, resolve) : apiType.displayName,
    count: apiType.count ?? 0,
    open: apiType.open ?? 0,
    done: apiType.done ?? 0,
    total: apiType.total ?? 0,
    order: apiType.order ?? meta?.order ?? Number.MAX_SAFE_INTEGER,
  };
}
